interface UserIdentity {
  name?: string | null;
  email?: string | null;
}

const MISSING_USER_PLACEHOLDER = "—";

const emailLocalPart = (email: string): string => {
  const localPart = email.split("@")[0];
  return localPart !== undefined && localPart !== "" ? localPart : email;
};

/**
 * The name to show for a user: their name, falling back to the local part of
 * their email, and finally to an em dash.
 */
export const userDisplayName = (
  user: UserIdentity | null | undefined
): string => {
  const name = user?.name?.trim();
  if (name) {
    return name;
  }
  const email = user?.email?.trim();
  if (email) {
    return emailLocalPart(email);
  }
  return MISSING_USER_PLACEHOLDER;
};

/** Whether an email address belongs to a (Google) service account. */
export const isServiceAccountEmail = (
  email: string | null | undefined
): boolean =>
  (email ?? "").trim().toLowerCase().endsWith(".gserviceaccount.com");

/**
 * The email to show as hover metadata, or an empty string to suppress it —
 * for a missing address or a service account, whose address is machine noise.
 */
export const hoverEmail = (user: UserIdentity | null | undefined): string => {
  const email = user?.email?.trim();
  if (!email || isServiceAccountEmail(email)) {
    return "";
  }
  return email;
};

/** Up to two uppercase initials derived from the user's display name. */
export const userInitials = (user: UserIdentity | null | undefined): string => {
  const displayName = userDisplayName(user);
  if (displayName === MISSING_USER_PLACEHOLDER) {
    return "??";
  }
  const initials = displayName
    .split(/[\s.]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2);
  return initials || "??";
};
